import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfig, appConfigService } from '@app/config';
import { Role } from '@app/shared/models/role';
import { AuthRequestOptions, NotificationService } from '@ntap/core';
import { throwError as _throw, BehaviorSubject, Observable, of } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  delay,
  filter,
  finalize,
  first,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  tap
} from 'rxjs/operators';
import { AppUser } from '../model/app-user';

export type EMPTY = Record<string, never>;

export const empty: EMPTY = {};

export function nonEmpty<T>(input: T | EMPTY): input is T {
  return input !== empty && !!input && Object.keys(input).length > 0;
}

const HAS_CSRF_TOKEN = 'hasCsrfToken';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public validUser$: Observable<AppUser>;

  public user$: Observable<AppUser | EMPTY>;

  private currentUser$: Observable<AppUser | EMPTY>;

  private emptyUser: EMPTY = {};

  private loggedInUser: BehaviorSubject<AppUser | EMPTY>;

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(appConfigService) private config: AppConfig,
    private notificationService: NotificationService
  ) {
    this.loggedInUser = new BehaviorSubject<AppUser | EMPTY>(this.emptyUser);
    this.currentUser$ = this.fetchUserData();
    this.user$ = this.loggedInUser.pipe(
      switchMap(() => {
        if (!this.hasSessionStorage()) {
          return of(this.emptyUser);
        }
        return this.currentUser$;
      }),
      catchError(() => of(this.emptyUser)),
      shareReplay(1),
      defaultIfEmpty({})
    );
    this.validUser$ = this.user$.pipe(filter(nonEmpty));
  }

  getCsrfToken(): Observable<boolean> {
    return this.http
      .get(
        this.config.ntapEnvironment?.csrfTokenEndpoint || '',
        new AuthRequestOptions()
      )
      .pipe(
        tap(() => {
          sessionStorage.setItem(HAS_CSRF_TOKEN, 'true');
        }),
        map((token) => !!token)
      );
  }

  login(credentials: {
    username: string;
    password: string;
  }): Observable<AppUser> {
    return this.getCsrfToken().pipe(
      switchMap(() => {
        const body = JSON.stringify({
          username: credentials.username,
          password: credentials.password
        });
        const endpoint =
          this.config.ntapEnvironment?.authenticationEndpointAuthenticate;
        return this.http
          .post(`${endpoint}`, body, new AuthRequestOptions())
          .pipe(
            mergeMap((response) => {
              this.nextUser(response as AppUser);
              return this.validUser$;
            })
          );
      })
    );
  }

  private nextUser(loginResponse: AppUser | EMPTY): void {
    const user = loginResponse ? loginResponse : {};
    if (Object.keys(user).length === 0) {
      this.clearsessionStorage();
    }
    this.loggedInUser.next(user);
  }

  logout(): void {
    const endpoint = this.config.apiHost + 'api/ntap/auth/logout';
    this.http
      .post(`${endpoint}`, {}, new AuthRequestOptions())
      .pipe(
        first(),
        finalize(() => {
          this.clearsessionStorage();
          this.nextUser(this.emptyUser);
        })
      )
      .subscribe({
        next: () => {
          this.notificationService.success('Uspješno ste odjavljeni.');
          this.router.navigate(['login']);
        },
        error: (error) => this._throwServerError(error)
      });
  }

  mustChangePassword(user: AppUser | EMPTY): boolean {
    return user.brojPromjenaLozinke == 0;
  }

  isUserLoggedIn(user: AppUser | EMPTY): boolean {
    return nonEmpty(user) && this.hasSessionStorage();
  }

  public hasSessionStorage(): boolean {
    return !!sessionStorage.getItem(HAS_CSRF_TOKEN);
  }

  public clearsessionStorage(): void {
    sessionStorage.removeItem(HAS_CSRF_TOKEN);
  }

  private _throwServerError(err: object): Observable<object> {
    if (err instanceof HttpErrorResponse) {
      return _throw(() => {
        return (
          err || {
            details: { description: 'Došlo je do greške na poslužitelju.' }
          }
        );
      });
    }
    return _throw(() => {
      return err || 'Došlo je do greške na poslužitelju.';
    });
  }

  private fetchUserData(): Observable<AppUser | EMPTY> {
    if (!this.config.ntapEnvironment) {
      console.error(
        'Configuration invalid. No NTAP enviroment settings found. Check NTAP initialization!'
      );
      return of(empty);
    }
    const url = this.config.ntapEnvironment.pathApiNtap + '/ui/me';
    return this.http.get(url, new AuthRequestOptions()).pipe(
      map((data: any) => {
        return data as AppUser;
      }),
      catchError(() => {
        this.router.navigate(['login']);
        return of(empty);
      })
    );
  }

  public userChangedPassword(credentials: {
    username: string;
    password: string;
  }): void {
    this.login(credentials)
      .pipe(
        delay(0),
        tap(() => {
          this.router.navigate(['']);
        })
      )
      .subscribe();
  }

  public static userHasAnyRole(user: AppUser | EMPTY, roles: Role[]) {
    return user.authorities.some(
      (a) => roles.indexOf(a.authority as Role) >= 0
    );
  }

  public userHasRole(user: AppUser | EMPTY, role: Role) {
    return AuthService.userHasAnyRole(user, [role]);
  }

  public static userIsAdminOrVoditeljZbirki(user: AppUser) {
    return AuthService.userHasAnyRole(user, [
      'Glavni administrator',
      'Administrator arhiva',
      'Administrator posjednika',
      'Voditelj popisa fondova i zbirki'
    ]);
  }

  logoutSilently() {
    this.clearsessionStorage();
    this.nextUser(this.emptyUser);
  }
}
